import {
  type AltchaWidgetElement,
  makeAltchaBoxClickable,
  setupAltcha,
  waitForAltcha,
} from "../utils/altcha";
import {
  type CodeInputController,
  type OtpErrorMessages,
  requestLoginCode,
  setupCodeInput,
  verifyLoginCode,
} from "../utils/otp";
import { initUserTypeRequired, initUserTypeWarnings } from "../utils/user-type-warnings";

export const initAddReference = () => {
  const addReference = document.getElementById("add-reference") as HTMLDivElement;

  if (!addReference) {
    return;
  }

  initPhotoUpload();

  const form = addReference.querySelector<HTMLFormElement>("form#add-reference");

  if (form) {
    const validateFields = initFieldErrors(form);
    initOtpFlow(addReference, validateFields);

    // Logged in: no code step, the form submits directly. Capture phase, so initFormValidation()
    // sees a cancelled submit and shows no spinner
    form.addEventListener(
      "submit",
      (event) => {
        if (!validateFields()) {
          event.preventDefault();
        }
      },
      { capture: true },
    );
  }

  initUserTypeChoice(addReference);
};

type Field = HTMLInputElement | HTMLTextAreaElement;

/**
 * A message above each field that is not filled in correctly ("Required to add.", or the field's
 * own message), as on the signup screen, instead of the browser's validation bubbles. Returns the
 * check to run on submit; a message goes away as soon as its field is changed.
 */
function initFieldErrors(form: HTMLFormElement): () => boolean {
  form.noValidate = true;

  const errors = Array.from(form.querySelectorAll<HTMLDivElement>("[data-field-error]"));
  const fieldOf = (error: HTMLDivElement) =>
    form.querySelector<Field>(`[name="${error.dataset.fieldError}"]`);

  errors.forEach((error) => {
    // The text rendered in the alert is the "required" message
    error.dataset.errorRequired = error.textContent?.trim() ?? "";

    const field = fieldOf(error);
    const hide = () => {
      error.classList.add("d-none");
      field?.classList.remove("is-invalid");
    };
    field?.addEventListener("input", hide);
    field?.addEventListener("change", hide);
  });

  const messageFor = (error: HTMLDivElement, field: Field): string | null => {
    if (field instanceof HTMLInputElement && field.type === "file") {
      return field.files?.length ? null : (error.dataset.errorRequired ?? null);
    }

    const value = field.value.trim();

    // Too short counts as not added (the reference text has a minimum length)
    if (value === "" || (field.minLength > 0 && value.length < field.minLength)) {
      return error.dataset.errorRequired ?? null;
    }

    if (
      field instanceof HTMLInputElement &&
      field.type === "email" &&
      field.validity.typeMismatch
    ) {
      return error.dataset.errorInvalid ?? null;
    }

    return null;
  };

  return () => {
    let firstInvalid: { error: HTMLDivElement; field: Field } | null = null;

    for (const error of errors) {
      const field = fieldOf(error);
      const message = field ? messageFor(error, field) : null;

      error.classList.toggle("d-none", message === null);
      field?.classList.toggle("is-invalid", message !== null);

      if (message !== null && field) {
        error.textContent = message;
        firstInvalid ??= { error, field };
      }
    }

    if (firstInvalid === null) {
      return true;
    }

    const { error, field } = firstInvalid;
    error.scrollIntoView({ behavior: "smooth", block: "center" });

    if (!(field instanceof HTMLInputElement && field.type === "file")) {
      field.focus({ preventScroll: true });
    }

    return false;
  };
}

/** Success page: a reference writer can choose to use Babysits themselves. */
function initUserTypeChoice(addReference: HTMLDivElement) {
  const userTypeChoice = addReference.querySelector<HTMLFormElement>("#reference-user-type");

  if (!userTypeChoice) {
    return;
  }

  initUserTypeWarnings(userTypeChoice);
  initUserTypeRequired(userTypeChoice);
}

function initOtpFlow(addReference: HTMLDivElement, validateFields: () => boolean) {
  const emailInput = addReference.querySelector("#referenceSignupEmail") as HTMLInputElement | null;

  // Only for non-logged-in users (email field exists)
  if (!emailInput) {
    return;
  }

  setupAltcha();

  const form = addReference.querySelector("#add-reference") as HTMLFormElement;
  const stepForm = addReference.querySelector(".step-form") as HTMLDivElement;
  const stepCode = addReference.querySelector(".step-code") as HTMLDivElement;
  const csrfRequestCode = (addReference.querySelector(".csrf-request-code") as HTMLInputElement)
    .value;
  const csrfVerifyCode = (addReference.querySelector(".csrf-verify-code") as HTMLInputElement)
    .value;

  const altchaWrapper = addReference.querySelector(".altcha-wrapper") as HTMLDivElement;
  const altchaWidget = addReference.querySelector(
    ".altcha-wrapper altcha-widget",
  ) as AltchaWidgetElement;
  makeAltchaBoxClickable(altchaWidget);

  const submitButton = addReference.querySelector("#reference-submit") as HTMLButtonElement;
  const submitButtonLabel = submitButton.innerHTML;

  // Prevent native form validation from triggering on the hidden altcha input
  submitButton.type = "button";

  let currentEmail = "";

  // Intercept button click for OTP flow
  submitButton.addEventListener("click", () => {
    // The fields first: the captcha and the code step only once the form is complete
    if (!validateFields()) {
      return;
    }

    submitButton.disabled = true;

    waitForAltcha(altchaWrapper, altchaWidget)
      .then((payload) => {
        if (!form.checkValidity()) {
          form.reportValidity();
          submitButton.disabled = false;
          return;
        }

        currentEmail = emailInput.value;

        sendCodeRequest(payload);
      })
      .catch(() => {
        submitButton.disabled = false;
        alert(errorMessages.generic);
      });
  });

  function getSpamFields() {
    const spamvalidation = addReference.querySelector(
      'input[name="spamvalidation"]',
    ) as HTMLInputElement | null;
    const yourLastName = addReference.querySelector(
      'input[name="your_last_name"]',
    ) as HTMLInputElement | null;
    return {
      spamvalidation: spamvalidation?.value || "",
      yourLastName: yourLastName?.value || "",
    };
  }

  function sendCodeRequest(altchaPayload: string) {
    requestLoginCode(currentEmail, csrfRequestCode, altchaPayload, getSpamFields(), errorMessages)
      .then((result) => {
        submitButton.disabled = false;

        if (!result.success) {
          alert(result.errorMessage);
          return;
        }

        showCodeStep();
      })
      .catch(() => {
        submitButton.disabled = false;
      });
  }

  // Code step elements
  const codeInputWrapper = stepCode.querySelector(".code-input-wrapper") as HTMLDivElement;
  const codeSpinner = stepCode.querySelector(".code-spinner") as HTMLDivElement;
  const codeError = stepCode.querySelector(".code-error") as HTMLDivElement;
  const codeErrorMessage = stepCode.querySelector(".code-error-message") as HTMLDivElement;
  const errorMessages: OtpErrorMessages = {
    generic: stepCode.dataset.genericError || "Something went wrong.",
    rateLimited: stepCode.dataset.rateLimitError || "Too many requests.",
  };
  let isVerifying = false;

  const codeInput: CodeInputController = setupCodeInput(
    codeInputWrapper,
    (code) => {
      if (!isVerifying) {
        submitCode(code);
      }
    },
    () => {
      codeError.classList.add("d-none");
    },
  );

  function showCodeStep() {
    stepForm.classList.add("d-none");
    stepCode.classList.remove("d-none");
    const codeDescription = stepCode.querySelector(".code-description") as HTMLParagraphElement;
    codeDescription.textContent = codeDescription.textContent
      ? codeDescription.textContent.replace(/\S+@\S+|(?=\.)/, currentEmail)
      : `We sent a code to ${currentEmail}.`;

    resetCodeInput();
  }

  function submitCode(code: string) {
    isVerifying = true;
    codeInput.disable();
    codeSpinner.classList.remove("d-none");
    codeError.classList.add("d-none");

    verifyLoginCode(currentEmail, code, csrfVerifyCode, errorMessages)
      .then((result) => {
        if (result.success) {
          // User is now authenticated — submit the original form
          form.submit();
          return;
        }

        codeErrorMessage.textContent = result.errorMessage || errorMessages.generic;
        codeError.classList.remove("d-none");
        resetCodeInput();
      })
      .catch(() => {
        codeErrorMessage.textContent = errorMessages.generic;
        codeError.classList.remove("d-none");
        resetCodeInput();
      });
  }

  function resetCodeInput() {
    isVerifying = false;
    codeSpinner.classList.add("d-none");
    codeInput.reset();
  }

  // Back button
  const backButton = stepCode.querySelector(".back") as HTMLButtonElement;
  backButton.addEventListener("click", () => {
    stepCode.classList.add("d-none");
    stepForm.classList.remove("d-none");
    submitButton.disabled = false;
    submitButton.innerHTML = submitButtonLabel;
  });

  // Resend code
  const resendCodeButton = stepCode.querySelector(".resend-code") as HTMLButtonElement;
  const resendAltchaWrapper = stepCode.querySelector(".resend-altcha-wrapper") as HTMLDivElement;
  const resendAltchaWidget = resendAltchaWrapper.querySelector(
    "altcha-widget",
  ) as AltchaWidgetElement;
  makeAltchaBoxClickable(resendAltchaWidget);

  resendCodeButton.addEventListener("click", () => {
    resendCodeButton.disabled = true;

    waitForAltcha(resendAltchaWrapper, resendAltchaWidget)
      .then((payload) => sendResendCodeRequest(payload))
      .catch(() => {
        resendCodeButton.disabled = false;
        alert(errorMessages.generic);
      });
  });

  function sendResendCodeRequest(altchaPayload: string) {
    requestLoginCode(currentEmail, csrfRequestCode, altchaPayload, getSpamFields(), errorMessages)
      .then((result) => {
        resendCodeButton.disabled = false;

        if (!result.success) {
          alert(result.errorMessage);
          return;
        }

        alert(resendCodeButton.dataset.successMessage || "Code sent!");
      })
      .catch(() => {
        resendCodeButton.disabled = false;
      });
  }
}

function initPhotoUpload() {
  const referencePhotoSelect = document.getElementById("referencePhotoSelect") as HTMLDivElement;
  const referencePhotoAdd = document.getElementById("referencePhotoAdd") as HTMLButtonElement;
  const referencePhotoInput = document.getElementById("referencePhotoInput") as HTMLInputElement;

  const referencePhotoPreview = document.getElementById(
    "referencePhotoPreview",
  ) as HTMLImageElement;

  if (referencePhotoSelect && referencePhotoAdd && referencePhotoInput && referencePhotoPreview) {
    referencePhotoSelect.addEventListener(
      "click",
      () => {
        if (referencePhotoInput) {
          referencePhotoInput.click();
        }
      },
      false,
    );
    referencePhotoAdd.addEventListener(
      "click",
      () => {
        if (referencePhotoInput) {
          referencePhotoInput.click();
        }
      },
      false,
    );

    referencePhotoInput.addEventListener(
      "change",
      (e: Event) => {
        const target = e.target as HTMLInputElement;
        const files = target.files;

        if (files) {
          const file = files[0];

          if (!file.type.startsWith("image/jpeg") && !file.type.startsWith("image/png")) {
            alert("Sorry, that's an invalid file type. Only JPG and PNG allowed.");
            return false;
          }

          previewFile(file);
        }
      },
      false,
    );

    referencePhotoSelect.addEventListener("dragenter", dragenter, false);
    referencePhotoSelect.addEventListener("dragleave", dragleave, false);
    referencePhotoSelect.addEventListener("dragover", dragover, false);
    referencePhotoSelect.addEventListener("drop", drop, false);

    function dragenter(e: Event) {
      referencePhotoSelect.classList.add("active");

      e.stopPropagation();
      e.preventDefault();
    }

    function dragleave(e: Event) {
      referencePhotoSelect.classList.remove("active");

      e.stopPropagation();
      e.preventDefault();
    }

    function dragover(e: Event) {
      referencePhotoSelect.classList.add("active");

      e.stopPropagation();
      e.preventDefault();
    }

    function drop(e: DragEvent) {
      referencePhotoSelect.classList.remove("active");
      e.stopPropagation();
      e.preventDefault();

      const dt = e.dataTransfer as DataTransfer;
      const files = dt.files;

      const file = files[0];

      if (!file.type.startsWith("image/jpeg") && !file.type.startsWith("image/png")) {
        alert("Sorry, that's an invalid file type. Only JPG and PNG allowed.");
        return false;
      }

      // A dropped photo was only previewed, never submitted: attach it to the form's file input
      referencePhotoInput.files = files;
      referencePhotoInput.dispatchEvent(new Event("change"));
    }

    function previewFile(file: Blob | MediaSource) {
      referencePhotoPreview.style.display = "block"; // After removing a photo it is set to display=none, so make it block here

      referencePhotoPreview.style.objectFit = "cover";
      referencePhotoPreview.style.height = "100%";

      referencePhotoPreview.src = URL.createObjectURL(file);

      referencePhotoPreview.onload = () => {
        URL.revokeObjectURL(referencePhotoPreview.src);
      };
    }
  }
}
